// src/views/adminView/contractsView/compliance/UsuariosEmpresaSection.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Usuarios del portal de clientes de una empresa — datos REALES desde
// useClientAccounts (users/clients + KAM/client del tenant, filtrado
// client-side por companyId). Reemplaza la lista ficticia de localStorage
// (bt.empresa.usuarios.<contractId>) que solo existía en este navegador.
//
// Es una VISTA: la administración (editar, bloquear, marcas de vendedor) vive
// en el tab Cuentas del hub — el botón "Administrar en Cuentas" salta allá con
// el filtro de esta empresa aplicado (onGoCuentas la baja desde el hub porque
// el detalle inline es estado local y un link a la misma ruta no lo cierra).
//
// users/clients solo cubre empresas con contrato ACTIVO: para el resto la
// sección no lista (mostraría un "sin usuarios" falso) y lo dice.
// ─────────────────────────────────────────────────────────────────────────────

import { useTranslation } from 'react-i18next';
import { ArrowRight, Users } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Card, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/Card';
import { Skeleton } from '../../../../components/ui/Skeleton';
import {
  canBeSeller, humanizeRole, initialsOf, roleTone,
} from '../../usersModule/usersConfig';
import { useClientAccounts } from '../useClientAccounts';

export default function UsuariosEmpresaSection({ contract, onGoCuentas }) {
  const { t } = useTranslation();
  const companyId = contract?.companyId?._id || null;
  const isActive = contract?.status === 'active';
  const nombreEmpresa = contract?.companyId?.name ?? '—';

  const { accounts, loading, listOf } = useClientAccounts();
  const usuarios = isActive && companyId ? listOf(companyId) : null;

  return (
    <Card padding="md">
      <CardHeader>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-fg-muted" aria-hidden="true" />
            <CardTitle>
              {Array.isArray(usuarios)
                ? t('Usuarios de la empresa · {{n}}', { n: usuarios.length })
                : t('Usuarios de la empresa')}
            </CardTitle>
          </div>
          <CardDescription className="mt-0.5">
            {t('Personas de {{nombre}} con acceso al portal de clientes.', {
              nombre: nombreEmpresa,
            })}
          </CardDescription>
        </div>
        {typeof onGoCuentas === 'function' && isActive && companyId && (
          <Button
            variant="outline"
            size="sm"
            iconRight={<ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />}
            onClick={() => onGoCuentas(companyId)}
          >
            {t('Administrar en Cuentas')}
          </Button>
        )}
      </CardHeader>

      {!isActive || !companyId ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-fg-muted">
          {!companyId
            ? t('No se encontró la empresa asociada al contrato.')
            : t('Disponible para empresas con contrato activo.')}
        </div>
      ) : loading ? (
        <div className="space-y-2" aria-hidden="true">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : accounts === null ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-fg-muted">
          {t('No pudimos cargar los usuarios. Reintenta en unos minutos.')}
        </div>
      ) : usuarios.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-fg-muted">
          {t('Esta empresa todavía no tiene usuarios en el portal.')}
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {usuarios.map((u) => {
            const bloqueada = u.active === false;
            return (
              <li key={u._id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                      bloqueada ? 'bg-bg-muted text-fg-muted' : 'bg-brand-100 text-brand-700',
                    )}
                  >
                    {initialsOf(u.name, u.lastName)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={cn(
                          'text-sm font-medium',
                          bloqueada ? 'text-fg-muted line-through' : 'text-fg',
                        )}
                      >
                        {u.name} {u.lastName}
                      </span>
                      {bloqueada && (
                        <Badge tone="error" dot>
                          {t('Acceso bloqueado')}
                        </Badge>
                      )}
                      {u.isSeller && canBeSeller(u) && (
                        <Badge tone="info">{t('Vendedor')}</Badge>
                      )}
                      {u.isSalesSupervisor && canBeSeller(u) && (
                        <Badge tone="warning">{t('Supervisor de ventas')}</Badge>
                      )}
                    </div>
                    <div className="truncate text-xs text-fg-muted">
                      {u.email || '—'}
                    </div>
                  </div>
                  <Badge tone={roleTone(u?.role?.name)}>
                    {humanizeRole(u?.role?.name_public || u?.role?.name || '—')}
                  </Badge>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
